import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Mmr, Person, UserProfile, webpages_Membership, webpages_Role } from 'app/shared/models/domain/data.model';
import { PageResponse } from '@iapmoit/library.angular.core';
import { environment } from 'environments/environment';
import { MmrLoginAccessView } from './models/mmr-login-access-view.model';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AdminMmrDetailsView } from './mmr-rep-type';
import { MmrClientView } from './models/mmr-client-view.model';

@Injectable({
    providedIn: 'root'
})
export class MmrService {
    baseUrl = environment.apiBaseUrl;
    constructor(private _httpClient: HttpClient) { }
    saveMmrClient(mmrId: number, mmrClient: Mmr): any {
        if (mmrId) {
            return this._httpClient.patch<PageResponse<Mmr>>(`${this.baseUrl}api/data/mmr/${mmrId}`, mmrClient);
        } else {
            return this._httpClient.post<PageResponse<Mmr>>(`${this.baseUrl}api/data/mmr`, mmrClient);
        }
    }
    getMmrList(request): Observable<any> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get(this.baseUrl + 'api/data/mmrs-view', { params: params });
    }
    getParams(request, params): any {
        params['itemsPerPage'] = request.itemsPerPage?.toString();
        params['currentPage'] = request.currentPage?.toString();
        if (request.sort && request.sortDir) {
            params[`sort[${request.sort}]`] = request.sortDir;
        }
        if (request.mmrId) {
            params['find[mmrId]'] = parseInt(request.mmrId);
        }
        if (request.sponsorLocationId) {
            params['find[sponsorLocationId]'] = request.sponsorLocationId;
        }
        if (request.clientMmrStatus) {
            params['find[clientMmrStatus]'] = request.clientMmrStatus;
        }
        if (request.clientMmrStatuses?.length) {
            params['find[clientMmrStatus:nin]'] = request.clientMmrStatuses.join();
        }
        if (request.name) {
            params['orlike[FirstName]'] = request.name;
            params['orlike[LastName]'] = request.name;
        }
        if (request.fullName) {
            params['orlike[FirstName]'] = request.fullName;
            params['orlike[LastName]'] = request.fullName;
            params['orlike[FullName]'] = request.fullName;
        }
        if (request.standardId && request.standardId?.length) {
            params['find[standardIds:split]'] = request.standardId.join();
        }
        if (request.clientStatusId && request.clientStatusId?.length) {
            params['find[salesStatusId:in]'] = request.clientStatusId.join();
        }
        if (request.loginAccess) {
            params['find[loginAccess]'] = request.loginAccess;
        }
        if (request.userId) {
            params['find[userId]'] = request.userId;
        }
        if (request.userId) {
            params['find[userId]'] = request.userId;
        }
        if (request.industryCodeId) {
            params['find[industryCodeId]'] = request.industryCodeId;
        }
        if (request.city) {
            params['orlike[city]'] = request.city;
        }
        if (request.stateId) {
            params['find[stateProvinceId]'] = request.stateId;
        }

        if (request['find[mmrId:nin]']) {
            params['find[mmrId:nin]'] = request['find[mmrId:nin]'];
        }

        const companyParam = request.searchParam || request.companyName || null;

        if (companyParam) {
            params['orLike[companyName]'] = companyParam;
            params['orLike[clientNumber]'] = companyParam;
        }

        return params;
    }

    attachAccountToMmr(profile: UserProfile, mmr: AdminMmrDetailsView): Observable<any> {
        if (profile && profile.email) {
            let profileTask: Observable<UserProfile> = null;
            //  if profile and userId already exists: just look it up
            if (profile.userId > 0) {
                profileTask = this._httpClient.get(`${this.baseUrl}api/data/user-profile/${profile.userId}`).pipe(map((data: PageResponse<UserProfile>) => data.item));
            } else {
                //  if profile does not yet exist we need to create it
                profileTask = forkJoin([
                    //  get MMR Rep role so we can attach it by id
                    this._httpClient.get(`${this.baseUrl}api/data/admin/roles?find[RoleName]=MMR`).pipe(map((data: PageResponse<webpages_Role>) => data.items[0]))
                    //  insert the new row in UserProfiles
                    , this._httpClient.post(`${this.baseUrl}api/data/user-profile`, profile).pipe(map((data: PageResponse<UserProfile>) => data.item))
                ]).pipe(
                    switchMap(([role, newProfile]: [webpages_Role, UserProfile]) => {
                        return forkJoin([
                            //  create membership to save password etc.
                            this._httpClient.post(`${this.baseUrl}api/data/member`, { userId: newProfile.userId }).pipe(map((data: PageResponse<webpages_Membership>) => { return data.item; }))
                            //  attach the role
                            , this._httpClient.post(`${this.baseUrl}api/data/admin/user-roles/${newProfile.userId}/${role.roleId}`, {})
                        ]).pipe(switchMap((data) => {
                            //  finally return the user we just created
                            return this._httpClient.get(`${this.baseUrl}api/data/user-profile/${newProfile.userId}`).pipe(map((data: PageResponse<UserProfile>) => data.item));
                        }));
                    }));
            }
            return profileTask
                .pipe(
                    switchMap((profileData: UserProfile) => {
                        //  now we have our user - attach to the Auditor
                        const payload: Mmr = mmr.mmr;
                        payload.userId = profileData.userId;
                        return this.saveMmrClient(payload.id, payload);
                    })
                );
        }
    }

    getMmrDetails(mmrId: number): Observable<AdminMmrDetailsView> {
        return this._httpClient.get<PageResponse<Mmr>>(`${this.baseUrl}api/data/mmr/${mmrId}`).pipe(
            switchMap((auditor: PageResponse<Mmr>) => {
                const tasks = [
                    (auditor.item.personId > 0) ? this._httpClient.get<PageResponse<Person>>(`${this.baseUrl}api/data/person/${auditor.item.personId}`).pipe(map((response) => { return response.item })) : of(null)
                    , this._httpClient.get<PageResponse<MmrLoginAccessView>>(`${this.baseUrl}api/data/mmr-login-access-view`, { params: { 'find[id]': mmrId } }).pipe(map((response) => { return response.items }))
                    , (auditor.item.userId > 0) ? this._httpClient.get<PageResponse<UserProfile>>(`${this.baseUrl}api/data/user-profile/${auditor?.item?.userId}`).pipe(map((response) => { return response.item })) : of(null)
                ]

                return forkJoin(tasks).pipe(
                    map(([person, loginAccess, user]: [Person, MmrLoginAccessView[], UserProfile]) => {
                        return {
                            mmr: auditor.item,
                            user: user,
                            person: person,
                            loginAccess: loginAccess[0]
                        } as AdminMmrDetailsView;
                    })
                );

            })
        );
    }

    getMmrClient(request): Observable<any> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<MmrClientView>>(`${this.baseUrl}api/data/mmr-client-view`, { params: params })
    }

    getSalesRepClient(request): Observable<any> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<MmrClientView>>(`${this.baseUrl}api/data/sales-rep/clients/view`, { params: params })
    }

    deleteMmr(id: number): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/mmr/${id}`);
    }
}
